import { Plus, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { formatPhoneNumber } from "@/lib/phoneFormat";

// Contacts list + add-form for the customer side-sheet. Renders one
// row per contact (with inline edit mode), a toggleable add-new form,
// and badges (Primary, Global) for visual context.
//
// State stays in the parent (`Clients.jsx`); this component receives
// already-resolved arrays (`visibleContacts`, `contactRoles`) and
// imperative handlers (`updateContact`, `removeContact`, `addContact`,
// `addContactRole`). Extracted from Clients.jsx (Feb 2026, iter177).
//
// The `isGlobal` per-contact computation is preserved 1:1 — a contact
// that's primary at the client level but currently viewed under a
// non-primary site shows the "Global" pill and locks itself read-only.
export function ContactsListSection({
  visibleContacts,
  formData,
  selectedSiteId, primarySite,
  editingContactId, setEditingContactId,
  isAddingContact, setIsAddingContact,
  newContact, setNewContact,
  contactRoles,
  updateContact,
  removeContact,
  addContact,
  addContactRole,
}) {
  return (
    <div data-testid="contacts-list-section">
      {visibleContacts.length === 0 && !isAddingContact && (
        <div className="text-xs text-slate-400">No contacts yet — add one with the + button.</div>
      )}
      {visibleContacts.length > 0 && (
        <div className={`space-y-2 mb-3 ${visibleContacts.length > 5 ? "max-h-[280px] overflow-y-auto pr-1" : ""}`}>
          {visibleContacts.map((contact, contactIdx) => {
            const isGlobal = selectedSiteId !== "all"
              && primarySite && primarySite.id !== selectedSiteId
              && contact.is_primary
              && !(formData.contacts || []).find((c) => c.id === contact.id);
            const isEditing = editingContactId === contact.id;
            // iter164 a11y/correctness: some legacy site contacts were
            // saved without an `id` (e.g. seeded data). Fall back to
            // email + index so React still gets a stable key per row.
            const contactKey = contact.id
              || `legacy-${contact.email || contact.name || ""}-${contactIdx}`;
            return (
              <div
                key={contactKey}
                className={`p-2 rounded ${isGlobal ? "bg-blue-50 border border-blue-100" : "bg-slate-50"}`}
              >
                {isEditing && !isGlobal ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={contact.name}
                        onChange={(e) => updateContact(contact.id, "name", e.target.value)}
                        placeholder="Name"
                        className="h-8 text-sm"
                        data-testid={`edit-contact-name-${contact.id}`}
                      />
                      <Input
                        value={contact.email || ""}
                        onChange={(e) => updateContact(contact.id, "email", e.target.value)}
                        placeholder="Email"
                        className="h-8 text-sm"
                      />
                      <Input
                        value={contact.phone || ""}
                        onChange={(e) => updateContact(contact.id, "phone", formatPhoneNumber(e.target.value))}
                        placeholder="303-333-3333"
                        className="h-8 text-sm"
                      />
                      <SearchableSelect
                        value={contact.role || "none"}
                        onValueChange={(v) => updateContact(contact.id, "role", v === "none" ? "" : v)}
                        options={contactRoles.map((r) => ({ value: r, label: r }))}
                        placeholder="Select role"
                        popoverWidth="220px"
                        showNone={true}
                        onAddNew={() => {
                          const name = window.prompt("New role name:");
                          if (name) {
                            const r = addContactRole(name);
                            if (r) updateContact(contact.id, "role", r);
                          }
                        }}
                        addLabel="Add new role"
                      />
                      <Input
                        value={contact.notes || ""}
                        onChange={(e) => updateContact(contact.id, "notes", e.target.value)}
                        placeholder="Notes"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={contact.is_primary}
                        onCheckedChange={(checked) => updateContact(contact.id, "is_primary", checked)}
                      />
                      <span className="text-xs text-slate-500">Primary</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs ml-auto"
                        onClick={() => setEditingContactId(null)}
                        data-testid={`done-edit-contact-${contact.id}`}
                      >Done</Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-2 flex-wrap"
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <User className="w-4 h-4 text-slate-400 shrink-0" />
                    <div
                      className="flex-1 min-w-0 cursor-pointer flex items-center gap-x-2 gap-y-0.5 flex-wrap"
                      onClick={() => !isGlobal && setEditingContactId(contact.id)}
                    >
                      <span className="text-sm font-medium truncate">{contact.name}</span>
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-brand hover:underline text-xs truncate"
                          onClick={(e) => e.stopPropagation()}
                        >{contact.email}</a>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone.replace(/\D/g, "")}`}
                          className="text-brand hover:underline text-xs whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >{contact.phone}</a>
                      )}
                      {contact.role && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">{contact.role}</span>
                      )}
                    </div>
                    {contact.notes && (
                      <div
                        className="text-xs text-slate-400 italic max-w-[200px] truncate shrink-0"
                        title={contact.notes}
                      >{contact.notes}</div>
                    )}
                    {contact.is_primary && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded shrink-0">
                        Primary
                      </span>
                    )}
                    {isGlobal && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded shrink-0">
                        Global
                      </span>
                    )}
                    {!isGlobal && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0 text-slate-300 hover:text-red-500"
                        onClick={() => removeContact(contact.id)}
                        data-testid={`remove-contact-${contact.id}`}
                      ><X className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {isAddingContact && (
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={newContact.name}
              onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              placeholder="Name"
              className="h-8"
              data-testid="input-detail-contact-name"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              value={newContact.email}
              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              placeholder="Email"
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input
              value={newContact.phone}
              onChange={(e) => setNewContact({ ...newContact, phone: formatPhoneNumber(e.target.value) })}
              placeholder="303-333-3333"
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <SearchableSelect
              value={newContact.role || "none"}
              onValueChange={(v) => setNewContact({ ...newContact, role: v === "none" ? "" : v })}
              options={contactRoles.map((r) => ({ value: r, label: r }))}
              placeholder="Select role"
              searchPlaceholder="Search roles..."
              showNone={true}
              popoverWidth="220px"
              onAddNew={() => {
                const name = window.prompt("New role name:");
                if (name) {
                  const r = addContactRole(name);
                  if (r) setNewContact((prev) => ({ ...prev, role: r }));
                }
              }}
              addLabel="Add new role"
            />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input
              value={newContact.notes}
              onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
              placeholder="Notes"
              className="h-8"
              data-testid="input-detail-contact-notes"
            />
          </div>
          <div className="flex gap-2 items-center col-span-2">
            <Checkbox
              checked={newContact.is_primary}
              onCheckedChange={(checked) => setNewContact({ ...newContact, is_primary: checked })}
            />
            <span className="text-xs">Primary</span>
            <Button
              type="button"
              size="sm"
              className="h-8"
              onClick={addContact}
              data-testid="btn-add-detail-contact"
            ><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
